// React and Third-party
import React, { useState, Fragment, useEffect, useCallback, useRef } from 'react';
import cogoToast from 'cogo-toast';
import NumberFormat from 'react-number-format';

// Components
import SuperCore from '../../../components/core/SuperCore';
import TableHeadSort from './TableHeadSort';
import AbonarModal from './AbonarModal';
import AbonarForm from './AbonarForm';
import PaginadorParent, { paginadorFilter } from '../../../components/paginador/PaginadorParent';

// Requests
import { getMetodosPago } from '../../../helpers/requests';
import { setAbono } from '../requests';

const abonarModalInfoInit = {
    id_inquilino: 0,
    id_vivienda: 0,
    inquilino: "",
    numero_registro: "",
    subfraccionamiento: ""
};

const modal = new SuperCore();
const abonarModalId = "TarjetaAbonar";

export default function BalancesTable(props) {
    const {
        balances,
        loadingBalances,
        loadingInquilinosMovimientos,
        desdeFecha,
        hastaFecha,
        onMovimientosSearch,
        onAbonoCompleted,
        HistorialComponent
    } = props;

    const [metodosPago, setMetodosPago] = useState([]);
    const [activeInquilinoHistorial, setActiveInquilinoHistorial] = useState(0);
    const [abonarModalInfo, setAbonarModalInfo] = useState(abonarModalInfoInit);
    const [loadingAbono, setLoadingAbono] = useState(false);
    const [loadingMetodosPago, setLoadingMetodosPago] = useState(false);
    const [prePagedData, setPrePagedData] = useState(balances);
    const [{ page, elementsPerPage }, setPaginadorInfo] = useState({ page: 0, elementsPerPage: 5 });
    const [keySort, setKeySort] = useState({ name: "", direction: "" });

    const pagedData = paginadorFilter(prePagedData, page, elementsPerPage);

    // Referencia al thead para medir su altura
    const theadRef = useRef(null);
    const [theadHeight, setTheadHeight] = useState(0);

    // Calcular altura del thead
    const calculateTheadHeight = useCallback(() => {
        if (theadRef.current) {
            setTheadHeight(theadRef.current.getBoundingClientRect().height);
        }
    }, []);

    useEffect(() => {
        calculateTheadHeight();
        window.addEventListener('resize', calculateTheadHeight);
        return () => window.removeEventListener('resize', calculateTheadHeight);
    }, [calculateTheadHeight]);

    useEffect(() => {
        calculateTheadHeight();
    }, [balances, calculateTheadHeight]);


    const sortByKey = (newOrder) => {
        const { name, direction } = newOrder;
        const sortedData = [...prePagedData]; // Copy pa no mutar

        sortedData.sort((a, b) => {
            let valA = a[name];
            let valB = b[name];


            if (name === 'saldo') {
                valA = parseFloat(valA) || 0;
                valB = parseFloat(valB) || 0;
                return direction === 'asc' ? valA - valB : valB - valA;
            }


            valA = (valA || '').toString().toLowerCase();
            valB = (valB || '').toString().toLowerCase();

            if (direction === 'asc') {
                return valA.localeCompare(valB, 'es', { sensitivity: 'base' });
            } else {
                return valB.localeCompare(valA, 'es', { sensitivity: 'base' });
            }
        });

        setKeySort(newOrder);
        setPrePagedData(sortedData);
        // Reseteo paginacioon a primera pag 
        setPaginadorInfo({ page: 0, elementsPerPage });
    };

    const loadMetodosPago = useCallback(async () => {
        setLoadingMetodosPago(true);
        const response = await getMetodosPago();
        if (response.error) {
            cogoToast.error(response.error, { position: "bottom-right", hideAfter: 5 });
        }
        if (response.message) {
            cogoToast.warn(response.message, { position: "bottom-right", hideAfter: 5 });
        }
        if (response.metodosPago) {
            const filteredMetodosPago = response.metodosPago.filter(metodo => metodo.Id_FormaPago !== -1);
            setMetodosPago(filteredMetodosPago);
        }
        setLoadingMetodosPago(false);
    }, []);

    // Actualizo datos y reseteo paginación
    useEffect(() => {
        setPrePagedData(balances);
        setPaginadorInfo({ page: 0, elementsPerPage });
    }, [balances]);

    useEffect(() => {
        loadMetodosPago();
    }, [loadMetodosPago]);

    const openInquilinoHistorial = (id_inquilino) => {
        const idToSet = activeInquilinoHistorial !== id_inquilino ? id_inquilino : 0;
        setActiveInquilinoHistorial(idToSet);
        if (activeInquilinoHistorial === id_inquilino) return;
        onMovimientosSearch(id_inquilino);
    };

    const abonarHandler = (inquilino) => {
        const abonoInfo = {
            inquilino: inquilino.inquilino,
            id_inquilino: inquilino.id_inquilino,
            id_vivienda: inquilino.id_vivienda,
            numero_registro: inquilino.numero_registro,
            subfraccionamiento: inquilino.subfraccionamiento,
        };
        setAbonarModalInfo(abonoInfo);
    };

    const loadAbonar = useCallback(async (formInfo) => {
        setLoadingAbono(true);
        const response = await setAbono(formInfo);
        if (response.error) {
            cogoToast.error(response.error, { position: "bottom-right", hideAfter: 5 });
        }
        setLoadingAbono(false);
        setAbonarModalInfo(abonarModalInfoInit);
        modal.superCloseModal(abonarModalId);
        if (response.created) {
            cogoToast.success("Movimiento Realizado", { position: "bottom-right", hideAfter: 5 });
            setActiveInquilinoHistorial(0);
            onAbonoCompleted();
        }
    }, [onAbonoCompleted]);

    const AbonarFormCallback = useCallback((props) => (
        <AbonarForm
            {...props}
            {...abonarModalInfo}
            ref={props.formRef}
            metodosPago={metodosPago}
            onSubmit={loadAbonar}
        />
    ), [abonarModalInfo, metodosPago, loadAbonar]);

    const LoadingContent = () => (
        <tr>
            <td className="text-center" colSpan={8} style={{ height: 200 }}>
                <i className="fas fa-spinner fa-spin"></i>
            </td>
        </tr>
    );

    const EmptyContent = () => (
        <tr>
            <td className="text-center" colSpan={8} style={{ height: 200 }}>
                <h4>Sin Balances</h4>
            </td>
        </tr>
    );

    return (
        <div className="card-table card-owners column" id="card-owners">
            {/* Contenedor scrollable para la tabla */}
            <div className="table-scroll-container" style={{ maxHeight: '70vh', overflow: 'auto' }}>
                <table className="full table-equal">
                    <colgroup>
                        <col width="20%" />
                        <col width="20%" />
                        <col width="20%" />
                        <col width="15%" />
                        <col width="15%" />
                        <col width="10%" />
                    </colgroup>
                    <thead ref={theadRef} style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                        <tr>
                            <th className="text-left">
                                <TableHeadSort
                                    title="Residente"
                                    orderName="inquilino"
                                    orderKey={keySort}
                                    onOrderKeyChange={(newOrder) => sortByKey(newOrder)}
                                    className="text-left"
                                />
                            </th>
                            <th className="text-left">
                                <TableHeadSort
                                    title="Subfraccionamiento"
                                    orderName="subfraccionamiento"
                                    orderKey={keySort}
                                    onOrderKeyChange={(newOrder) => sortByKey(newOrder)}
                                    className="text-left"
                                />
                            </th>
                            <th className="text-left">
                                <TableHeadSort
                                    title="Vivienda"
                                    orderName="numero_registro"
                                    orderKey={keySort}
                                    onOrderKeyChange={(newOrder) => sortByKey(newOrder)}
                                    className="text-left"
                                />
                            </th>
                            <th className="text-right">
                                <TableHeadSort
                                    title="Balance"
                                    orderName="saldo"
                                    orderKey={keySort}
                                    onOrderKeyChange={(newOrder) => sortByKey(newOrder)}
                                    className="justify-end"
                                />
                            </th>
                            <th className="text-center">Historial</th>
                            <th className="text-center">Abonar</th>
                        </tr>
                    </thead>
                    <tbody className="extra-padding">
                        {loadingBalances ? (
                            <LoadingContent />
                        ) : pagedData.length === 0 ? (
                            <EmptyContent />
                        ) : (
                            <TableContent
                                content={pagedData}
                                activeInquilinoHistorial={activeInquilinoHistorial}
                                loadingMovimientos={loadingInquilinosMovimientos}
                                abonarHandler={abonarHandler}
                                openInquilinoHistorial={openInquilinoHistorial}
                                desdeFecha={desdeFecha}
                                hastaFecha={hastaFecha}
                                HistorialComponent={HistorialComponent}
                                theadHeight={theadHeight}
                            />
                        )}
                    </tbody>
                </table>
            </div>

            {!loadingBalances && (
                <PaginadorParent
                    data={prePagedData}
                    onPagedDataChange={({ page, elementsPerPage }) => setPaginadorInfo({ page, elementsPerPage })}
                />
            )}

            <AbonarModal
                id={abonarModalId}
                id_inquilino={abonarModalInfo.id_inquilino}
                loadingAbono={loadingAbono}
                onCloseModal={() => setAbonarModalInfo(abonarModalInfoInit)}
                AbonarFormComponent={AbonarFormCallback}
            />
        </div>
    );
}

function TableContent(props) {
    const {
        content,
        activeInquilinoHistorial,
        loadingMovimientos,
        abonarHandler,
        openInquilinoHistorial,
        desdeFecha,
        hastaFecha,
        HistorialComponent,
        theadHeight,
    } = props;

    return content.map((inquilino, index) => (
        <Fragment key={inquilino.id_tarjeta}>
            <tr
                className={activeInquilinoHistorial === inquilino.id_inquilino ? "row-active" : undefined}
                style={
                    activeInquilinoHistorial === inquilino.id_inquilino
                        ? {
                            position: "sticky",
                            top: theadHeight,
                            backgroundColor: "#E7E7E7",
                            zIndex: 2,
                        }
                        : undefined
                }
            >
                <td className="text-left">{inquilino.propietario || inquilino.inquilino}</td>
                <td className="text-left">{inquilino.subfraccionamiento}</td>
                <td className="text-left">{inquilino.numero_registro}</td>
                <td className="text-right">
                    <NumberFormat
                        value={inquilino.saldo}
                        displayType="text"
                        thousandSeparator={true}
                        prefix="$"
                        decimalScale={2}
                        fixedDecimalScale={true}
                    />
                </td>
                <td className="text-center">
                    <button
                        className="btn-full btn-mini btn-secondary color-white"
                        disabled={loadingMovimientos}
                        type="button"
                        onClick={() => openInquilinoHistorial(inquilino.id_inquilino)}
                    >
                        {loadingMovimientos && activeInquilinoHistorial === inquilino.id_inquilino ? (
                            <i className="fas fa-spinner fa-spin"></i>
                        ) : (
                            <i
                                className={
                                    "font-small fas fa-info-circle " +
                                    (activeInquilinoHistorial === inquilino.id_inquilino ? "color-red" : "")
                                }
                            />
                        )}
                    </button>
                </td>
                <td className="text-center">
                    <button
                        className="btn btn-primary btn-red-table color-white"
                        onClick={() => abonarHandler(inquilino)}
                    >
                        Abonar
                    </button>
                </td>
            </tr>
            {!loadingMovimientos &&
                activeInquilinoHistorial !== 0 &&
                activeInquilinoHistorial === inquilino.id_inquilino && (
                    <tr key={inquilino.id_inquilino + "mov"} className="acordeon-row" style={{ backgroundColor: "#FFF" }}>
                        <td className="acordeon" colSpan="9">
                            <HistorialComponent
                                inquilino={inquilino.inquilino}
                                dateRange={{
                                    desde: desdeFecha,
                                    hasta: hastaFecha,
                                }}
                            />
                        </td>
                    </tr>
                )}
        </Fragment>
    ));
}